/**
 * src/views/focus/RemindersDue.jsx — TICKET-735 reminder notes due/overdue.
 * Reads notes.reminders_due (note_type=reminder, due_at within 7 days or past)
 * and lists them at the top of Focus. Tapping a row opens the task the reminder
 * was written on (task-scoped notes); project/bundle reminders show without a
 * task link. Renders nothing when there is nothing due — same contract as
 * AssignmentBanner.
 */

import { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { listRemindersDue } from '../../lib/api/notes';
import { useT } from '../../lib/i18n/I18nContext';

export default function RemindersDue({ onOpenTask, refreshKey = 0 }) {
  const { t } = useT();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    listRemindersDue()
      .then((data) => { if (alive) setRows(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setRows([]); }); // decoration on Focus; the task log is the truth
    return () => { alive = false; };
  }, [refreshKey]);

  if (rows.length === 0) return null;
  const now = Date.now();

  return (
    <div data-testid="reminders-due" style={{
      margin: '12px 16px 0', padding: '10px 12px', borderRadius: 12,
      background: 'var(--glass)', border: '1px solid var(--bdr)',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--t1)' }}>
        <Bell size={14} /> {t('notes.remindersTitle')} · {rows.length}
      </div>
      {rows.map((n) => {
        const overdue = new Date(n.due_at).getTime() < now;
        const clickable = !!(n.task_id && onOpenTask);
        return (
          <button
            key={n.id}
            type="button"
            data-testid={`reminder-row-${n.id}`}
            data-overdue={overdue ? '1' : '0'}
            onClick={() => { if (clickable) onOpenTask(n.task_id); }}
            disabled={!clickable}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 44,
              padding: '6px 8px', borderRadius: 8, border: 'none', textAlign: 'left',
              background: 'var(--card)', cursor: clickable ? 'pointer' : 'default',
            }}
          >
            <span style={{
              fontSize: 11, fontWeight: 700, flexShrink: 0,
              color: overdue ? 'var(--bad)' : 'var(--t2)',
            }}>
              {new Date(n.due_at).toLocaleDateString()}{overdue ? ` · ${t('notes.remindersOverdue')}` : ''}
            </span>
            <span style={{ fontSize: 13, color: 'var(--t1)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {n.body}
            </span>
            {clickable && <span style={{ fontSize: 11, color: 'var(--accent)', flexShrink: 0 }}>{t('notes.remindersOpenTask')}</span>}
          </button>
        );
      })}
    </div>
  );
}
